/**
 * Blog Post Page
 *
 * Single blog post view with full content and SEO optimization.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBlogPost, type BlogPost } from '../../lib/api';
import { useSEO, useStructuredData, SITE_CONFIG } from '../../lib/seo';
import PublicLayout from '../../components/layout/PublicLayout';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation('blog');
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // SEO - dynamic based on post data
  useSEO({
    title: post ? `${post.meta_title || post.title} | xtFORM Blog` : 'Loading... | xtFORM Blog',
    description: post?.meta_description || post?.excerpt || 'Read this article on xtFORM Blog',
    keywords: post?.meta_keywords || undefined,
    url: `${SITE_CONFIG.domain}/blog/${slug}`,
    locale: post?.locale || i18n.language,
    type: 'article',
    image: post?.featured_image || undefined,
  });

  // Article structured data
  const articleStructuredData = post ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': post.title,
    'description': post.excerpt || post.title,
    'image': post.featured_image || `${SITE_CONFIG.domain}/og-image.png`,
    'datePublished': post.published_at,
    'dateModified': post.updated_at,
    'author': {
      '@type': 'Organization',
      'name': SITE_CONFIG.name,
      'url': SITE_CONFIG.domain,
    },
    'publisher': {
      '@type': 'Organization',
      'name': SITE_CONFIG.name,
      'url': SITE_CONFIG.domain,
      'logo': {
        '@type': 'ImageObject',
        'url': `${SITE_CONFIG.domain}/logo.png`,
      },
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `${SITE_CONFIG.domain}/blog/${slug}`,
    },
    'wordCount': post.content.split(/\s+/).length,
    'articleSection': post.category,
    'keywords': post.tags?.join(', ') || post.meta_keywords,
  } : null;

  useStructuredData(articleStructuredData, 'blog-article');

  useEffect(() => {
    async function loadPost() {
      if (!slug) return;

      try {
        setLoading(true);
        const response = await getBlogPost(slug, i18n.language);
        setPost(response.post);
      } catch (err) {
        console.error('Failed to load blog post:', err);
        setError(err instanceof Error ? err.message : 'Post not found');
      } finally {
        setLoading(false);
      }
    }

    loadPost();
  }, [slug, i18n.language]);

  if (loading) {
    return (
      <PublicLayout bgClass="bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 py-16">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-4" />
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-8" />
            <div className="h-64 bg-gray-200 rounded mb-8" />
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (error || !post) {
    return (
      <PublicLayout bgClass="bg-gray-50">
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('public.post.notFound.title')}</h1>
            <p className="text-gray-500 mb-6">{error || t('public.post.notFound.description')}</p>
            <Link
              to="/blog"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              {t('public.post.notFound.backButton')}
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout bgClass="bg-gray-50">
      {/* Article */}
      <article className="max-w-3xl mx-auto px-4 py-12">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-blue-600">{t('public.post.breadcrumb.home')}</Link>
            </li>
            <li>/</li>
            <li>
              <Link to="/blog" className="hover:text-blue-600">{t('public.post.breadcrumb.blog')}</Link>
            </li>
            <li>/</li>
            <li className="text-gray-900 truncate max-w-xs">{post.title}</li>
          </ol>
        </nav>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
              {t(`public.categories.${post.category}`)}
            </span>
            <span className="text-gray-500 text-sm">
              {t('public.readTime', { minutes: post.reading_time_minutes })}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="text-xl text-gray-600 mb-4">
              {post.excerpt}
            </p>
          )}
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span>
              {t('public.post.published')} {post.published_at
                ? new Date(post.published_at).toLocaleDateString(i18n.language, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : t('public.draft')}
            </span>
          </div>
        </header>

        {/* Featured Image */}
        {post.featured_image && (
          <figure className="mb-8 -mx-4 md:mx-0">
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-auto rounded-lg shadow-md"
            />
          </figure>
        )}

        {/* Content */}
        <div
          className="prose prose-lg"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('public.post.tags')}</h2>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 p-8 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl text-white text-center">
          <h2 className="text-2xl font-bold mb-2">{t('public.post.cta.title')}</h2>
          <p className="text-blue-100 mb-6">
            {t('public.post.cta.description')}
          </p>
          <Link
            to="/register"
            className="inline-block px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
          >
            {t('public.post.cta.button')}
          </Link>
        </div>

        {/* Back to Blog */}
        <div className="mt-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('public.post.backToBlog')}
          </Link>
        </div>
      </article>
    </PublicLayout>
  );
}
